import { matchPath, generatePath } from 'react-router';

import type { SagaIterator } from 'redux-saga';
import { eventChannel, END } from 'redux-saga';
import { call, put, take, takeLatest, race, delay, select } from 'redux-saga/effects';

import { PATH } from 'constants/RoutesConstants';
import * as authActions from 'modules/auth/actions';
import * as types from 'modules/auth/actionTypes';
import { LOGIN_STEPS } from 'modules/auth/constant';
import { getLoginUsername } from 'modules/auth/selectors';
import type { MagicLinkPayload, SetAuthStepAction } from 'modules/auth/types';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { getLocation } from 'modules/router/selectors';
import {
  getBroadcastChannel,
  closeBroadcastChannel,
  sendBroadcastMessage,
  SUCCESS_LOGIN_MESSAGE
} from 'utils/broadcastChannel';

const MAGIC_LINK_TIMEOUT_MS = 5000;

// --- Sender (magic link tab) ---

const createSenderChannel = (username: string) =>
  eventChannel<string>(emitter => {
    const bc = getBroadcastChannel(username);

    bc.onmessage = (event: MessageEvent) => {
      if (event.data === SUCCESS_LOGIN_MESSAGE) {
        emitter(SUCCESS_LOGIN_MESSAGE);
        emitter(END);
      }
    };

    return () => {
      closeBroadcastChannel(username);
    };
  });

const createReceiverChannel = (username: string) =>
  eventChannel<string>(emitter => {
    const bc = getBroadcastChannel(username);

    bc.onmessage = (event: string) => {
      const receivedOtp = event;
      if (receivedOtp && receivedOtp !== SUCCESS_LOGIN_MESSAGE) {
        emitter(receivedOtp);
      }
    };

    return () => {
      bc.onmessage = null;
    };
  });

function* handleMagicLinkSender(payload: MagicLinkPayload): SagaIterator {
  const { otp, username, expireOn } = payload;
  const hasUndefinedValue = !otp || !username || !expireOn;

  if (hasUndefinedValue) {
    yield call(navigate, generatePath(PATH.HOME.path), { replace: true });
    return;
  }

  const expiration = new Date(expireOn).getTime();
  if (Number.isNaN(expiration) || Date.now() > expiration) {
    yield call(navigate, generatePath(PATH.HOME.path), { replace: true });
    return;
  }

  const bc = getBroadcastChannel(username);
  bc.postMessage(otp);

  const channel = yield call(createSenderChannel, username);

  const { timeout } = yield race({
    success: take(channel),
    timeout: delay(MAGIC_LINK_TIMEOUT_MS),
  });

  if (timeout) {
    channel.close();
  }

  yield call(navigate, generatePath(PATH.HOME.path), { replace: true });
}

// --- Receiver (login tab) ---

function* handleBroadcastChannelReceiver({ payload }: SetAuthStepAction): SagaIterator {
  const { step } = payload;
  const username: string | undefined = yield select(getLoginUsername);

  if (step !== LOGIN_STEPS.MFA_EMAIL || !username) {
    return;
  }

  const channel = yield call(createReceiverChannel, username);
  try {
    while (true) {
      const { otp, done } = yield race({
        otp: take(channel),
        done: take([types.SIGN_IN_SUCCESS, types.SIGN_IN_FAILURE]),
      });

      if (done) {
        if (done.type === types.SIGN_IN_SUCCESS) {
          sendBroadcastMessage(username, SUCCESS_LOGIN_MESSAGE);
          yield delay(100);
          closeBroadcastChannel(username);
        }
        break;
      }

      if (otp) {
        yield put(authActions.verifySignIn(otp));
      }
    }
  } finally {
    channel.close();
  }
}

function parseMagicLinkParams(search: string): MagicLinkPayload {
  const searchParams = new URLSearchParams(search);

  const otp = searchParams.get(PATH.MAGIC_LINK.searchParams.otp) ?? '';
  const username = searchParams.get(PATH.MAGIC_LINK.searchParams.username)?.toLowerCase().trim() ?? '';
  const expireOn = searchParams.get(PATH.MAGIC_LINK.searchParams.expireOn) ?? '';

  return { otp, username, expireOn };
}

function* onLocationChange(): SagaIterator {
  const location: { pathname: string; search: string } = yield select(getLocation);

  if (matchPath(PATH.MAGIC_LINK.path, location.pathname)) {
    yield call(handleMagicLinkSender, parseMagicLinkParams(location.search));
  }
}

export default function* magicLinkSaga(): SagaIterator {
  yield takeLatest(LOCATION_CHANGE, onLocationChange);
  yield takeLatest(types.SET_AUTH_STEP, handleBroadcastChannelReceiver);
}
